import { Cascader, Checkbox, Flex, Radio, Select, Space, Typography } from 'antd';
import { StyledCard } from 'src/styled_components/StyledCard';
import { StoreState } from 'src/store/configureStore';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { Samples } from 'src/mocks/db';
import { ReactGrid } from '@silevis/reactgrid';
import { useParams } from 'react-router-dom';
import { StyledButton } from 'src/styled_components/StyledButton';
import { trainingRequest } from 'src/store/actions/operations';

interface Option {
  value: string | number;
  label: string;
  children?: Option[];
  disableCheckbox?: boolean;
}

const MODELS = [
  { value: "linear", "label": "Linear" },
  { value: "huber", "label": "Huber" },
  { value: "theilsen", "label": "Thielsen" },
  { value: "ransac", "label": "Ransac" },
  { value: "gaussian_process", "label": "Gaussian Process" },
  { value: "gradient_boosting", "label": "Gradient Boosting" },
  { value: "xgboost", "label": "XGBoost" },
]

export const Filter = ({ currentStep, previous, next, steps }: any) => {
  const { anid, opid } = useParams<any>()
  const dispatch = useDispatch()
  const categories = useSelector((state: StoreState) => state.operations.analysisDetailsData.categories)
  const combinedDataset = useSelector((state: StoreState) => state.operations.analysisDetailsData.dataset)
  const combinedParentCategories = useMemo(() => categories
    ?.filter((category: any) => category.parentCategory)
    ?.reduce((acc: any, category: any) => {
      if (!acc[category.parentCategory]?.includes(category.name)) {
        acc[category.parentCategory] = [
          ...(acc[category.parentCategory] || []),
          category.name
        ];
      }
      return acc;
    }, []), [categories]);
  const combinedCategories = useMemo(() => categories
    ?.filter((category: any) => category.name)
    ?.reduce((acc: any, category: any) => ({
      ...acc,
      [category.name]: [
        ...(acc[category.name] || []),
        ...category.parameter_ids
      ]
    }), []), [categories])
  const options: Option[] = useMemo(() => Object.entries(combinedParentCategories || {})
    ?.filter(([parentCategory]: any) => parentCategory)
    ?.map(([parentCategory, _category]: any) => ({
      label: parentCategory,
      value: parentCategory,
      children: Object.entries(combinedCategories || {})
        ?.filter(([category]: any) => _category.includes(category))
        ?.map(([category, features]: any) => ({
          label: category,
          value: category,
          children: features
            ?.map((feature: string) => ({
              label: Samples.findOne({ id: feature })?.name ?? feature,
              value: feature
            }))
        }))
    })
    ), [combinedCategories, combinedParentCategories])

  const [filterType, setFilterType] = useState("include")
  const [features, setFeatures] = useState<string[]>(Object.values(combinedCategories || {})?.flat() as any || [])
  useEffect(() => {
    setFeatures(Object.values(combinedCategories || {})?.flat() as any || [])
  }, [combinedCategories])
  const selectedOptions = useMemo(() => Object.entries(combinedCategories || {})
    .filter(([category]) => category)
    .filter(([category, _features]: any) => _features.some((feature: string) => features.includes(feature)))
    .reduce((acc: any, [category, _features]: any) => {
      const parentCategory = categories.find((cat: any) => cat.name === category)?.parentCategory;
      return _features.every((feature: string) => features.includes(feature))
        ? [...acc, [parentCategory, category]]
        : [...acc, ..._features.filter((feature: string) => features.includes(feature)).map((feature: string) => [parentCategory, category, feature])]
    }, [])
    , [combinedCategories, features, categories]);

  const onChange = (options: any[]) => {
    let features = [];
    let categories = [];
    let parentCategories = [];
    for (const option of options) {
      if (option.length === 1) {
        parentCategories.push(option[0]);
      } else if (option.length === 2) {
        categories.push(option[1]);
      } else if (option.length === 3) {
        features.push(option[2]);
      }
    }

    if (parentCategories.length) {
      for (const parentCategory of parentCategories) {
        const categories = combinedParentCategories[parentCategory] || [];
        for (const category of categories) {
          features.push(combinedCategories[category]);
        }
      }
    }

    if (categories.length) {
      for (const category of categories) {
        features.push(combinedCategories[category]);
      }
    }

    setFeatures(features.flat());
  };

  const categorizedFeatures = useMemo(() => combinedDataset?.features?.filter((feature: string) => categories?.filter((category: any) => category.name).find((category: any) => category.parameter_ids.includes(feature))), [categories, combinedDataset?.features])
  const filteredFeatures = useMemo(() => filterType === "include" ? features : categorizedFeatures?.filter((feature: string) => !features.flat()?.includes(feature)), [categorizedFeatures, features, filterType])
  const columns = useMemo(() => [{ columnId: "Experiments", width: 150 }, ...filteredFeatures?.map((feature: string) => ({ columnId: feature, width: 150 }))], [filteredFeatures]);
  const rows = useMemo(() => {
    return [
      { // header row
        rowId: "PARENT_CATEGORY",
        cells: [
          { type: "header", text: "Category" },
          ...filteredFeatures?.map((feature: string) => {
            const category = categories.find((cat: any) => cat.parameter_ids.includes(feature));
            return { type: "text", text: category?.parentCategory ?? "" };
          })
        ]
      },
      { // header row
        rowId: "CATEGORY",
        cells: [
          { type: "header", text: "Sub-Category" },
          ...filteredFeatures?.map((feature: string) => {
            const category = categories.find((cat: any) => cat.parameter_ids.includes(feature));
            return { type: "text", text: category?.name ?? "" };
          })
        ]
      },
      { // header row
        rowId: "HEADER",
        cells: [{ type: "header", text: "Experiment" }, ...filteredFeatures?.map((feature: string) => ({ type: "header", text: Samples.findOne({ id: feature })?.name ?? feature }))]
      },
      ...combinedDataset?.data // body rows
        ?.map((row: any, expIdx: number) => ({
          rowId: combinedDataset?.experiments?.[expIdx],
          cells: [
            {
              type: 'text',
              text: Samples.findOne({ id: combinedDataset?.experiments?.[expIdx] })?.name ?? combinedDataset?.experiments?.[expIdx] ?? expIdx
            },
            ...filteredFeatures
              ?.map((feature: any) => ({
                type: 'text',
                text: String(row[feature])
              }))]
        })) || []]
  }, [filteredFeatures, categories, combinedDataset?.data, combinedDataset?.experiments]);

  const [targetFeatures, setTargetFeatures] = useState<string[]>([])
  const [modelTypes, setModelTypes] = useState<string[]>(() => MODELS.map(model => model.value))

  const [sameCategoryCombo, setSameCategoryCombo] = useState(true);
  const generateModels = () => {
    dispatch(trainingRequest({
      opid,
      anid,
      "target_features": targetFeatures,
      "category_selection": categories
        .filter((category: any) => category.parameter_ids
          .some((parameter: string) => filteredFeatures.includes(parameter))
        ),
      "features": filteredFeatures,
      "same_category_combo": sameCategoryCombo,
      "models": modelTypes,
      // "run_dwt_processing": false,
    }));
  }


  return (
    <StyledCard title="Input Categorisation">
      <Space direction='vertical' style={{ width: "100%" }}>
        <Checkbox checked={!sameCategoryCombo} onChange={(e) => setSameCategoryCombo(!e.target.checked)}>Sub-categories from the same categories should not be an allowed combination.</Checkbox><br />
        <Typography.Title level={5}>Filter:</Typography.Title>
        <Cascader
          style={{ width: "100%" }}
          options={options}
          onChange={onChange}
          value={selectedOptions}
          multiple
          maxTagCount="responsive"
        />
        <Flex justify='flex-end'>
          <Radio.Group
            size='small'
            optionType="button"
            buttonStyle="solid"
            defaultValue={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            value={filterType}
            options={[
              { value: "include", label: "Include" },
              { value: "exclude", label: "Exclude" },
            ]}
          />
        </Flex>
        <div style={{ width: "100%", overflow: "auto" }}>
          <ReactGrid enableRangeSelection stickyLeftColumns={1} rows={rows} columns={columns} />
        </div>
        <Typography.Title level={5}>Target features:</Typography.Title>
        <Select
          mode="multiple"
          options={combinedDataset?.features?.map((feature: string) => ({ label: Samples.findOne({ id: feature })?.name, value: feature }))}
          style={{ width: "100%" }}
          onChange={(values: any) => setTargetFeatures(values)}
          value={targetFeatures}
          optionFilterProp="label"
        />
        <Typography.Title level={5}>Model Types:</Typography.Title>
        <Select
          mode="multiple"
          options={MODELS}
          style={{ width: "100%" }}
          onChange={(values: any) => setModelTypes(values)}
          value={modelTypes}
          optionFilterProp="label"
          allowClear
        />
        <Flex style={{ marginTop: 24 }} justify="space-between">
          <div>
            {currentStep > 0 && (
              <StyledButton size="small" style={{ margin: '0 8px' }} onClick={previous}>
                Previous
              </StyledButton>
            )}
          </div>
          <Space>
            {/* {(
              <StyledButton size="small" onClick={save}>
                Save
              </StyledButton>
            )} */}
            {currentStep < steps.length - 1 && (
              <StyledButton size="small" type="primary" onClick={generateModels}>
                Generate Models
              </StyledButton>
            )}
          </Space>
        </Flex>
      </Space>
    </StyledCard>
  )
}
